import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { fetchPolicyById } from "../policies/policySlice";
import { getVehicleTypeDisplayName } from "../../utils/vehicleTypeMapper";

// MUI
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Fab from "@mui/material/Fab";

import PersonIcon from "@mui/icons-material/Person";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import BusinessIcon from "@mui/icons-material/Business";
import TagIcon from "@mui/icons-material/LocalOffer";
import ListAltIcon from "@mui/icons-material/ListAlt";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import HandymanIcon from "@mui/icons-material/Handyman";
import NumbersIcon from "@mui/icons-material/Numbers";
import PercentIcon from "@mui/icons-material/Percent";
import PhoneIcon from "@mui/icons-material/Phone";
import EditIcon from "@mui/icons-material/Edit";
import MobileCommissionEditDialog from "./MobileCommissionEditDialog";
import { useResponsiveFonts } from "../../utils/responsiveFonts";
import { navigateWithScrollReset } from "../../utils/ScrollRestoration";
import { createWhatsAppMessage } from "../../utils/urlShortener";

// Icons for bottom navigation
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SouthWestIcon from '@mui/icons-material/SouthWest';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const MobileCommissionDetail = () => {
    const dispatch = useDispatch();
    const { id: policyId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const { currentPolicy } = useSelector((state) => state.policies);
    const fonts = useResponsiveFonts() || {
        header: { xs: '12px', sm: '14px', md: '16px' },
        title: { xs: '10px', sm: '12px', md: '14px' },
        body: { xs: '8px', sm: '9px', md: '10px' },
        caption: { xs: '4px', sm: '5px', md: '6px' },
        button: { xs: '6px', sm: '7px', md: '8px' }
    };
    const contentRef = useRef(null);

    const fromPage = location.state?.fromPage || 1;
    const fromScreen = location.state?.fromScreen;
    const fromCategory = location.state?.fromCategory || 'motor';

    const [policy, setPolicy] = useState(location.state?.policy || null);
    const [loading, setLoading] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);

    const fetchPolicy = async () => {
        try {
            // Only set loading if we don't have policy data yet (initial load)
            if (!policy) setLoading(true);
            const userId = policy?.agentId || user?.uid || location.state?.userId || location.state?.policy?.agentId;

            // If we don't have userId or policyId yet, just return and wait for next render
            if (!userId || !policyId) {
                if (!loading) { // Avoid spamming log while loading
                    console.log("Waiting for userId or policyId...", { userId, policyId });
                }
                setLoading(false);
                return;
            }

            const result = await dispatch(fetchPolicyById({ agentId: userId, id: policyId }));
            const fetchedPolicy = result.payload;

            if (fetchedPolicy) {
                setPolicy(fetchedPolicy);
            } else {
                const policyRef = doc(db, `agents/${userId}/policies/${policyId}`);
                const snap = await getDoc(policyRef);

                if (snap.exists()) {
                    const data = snap.data();
                    console.log("📄 Policy fetched from Firestore:", data);
                    setPolicy({ id: snap.id, ...data });
                } else {
                    console.warn("❌ Policy not found at path.");
                }
            }
        } catch (err) {
            console.error("🔥 Error fetching policy:", err);
        } finally {
            setLoading(false);
        }
    };

    // Reset/Initialize policy when route changes
    useEffect(() => {
        if (location.state?.policy) {
            setPolicy(location.state.policy);
        } else {
            setPolicy(null);
        }
    }, [policyId, location.state]);

    useEffect(() => {
        const effectiveUserId = location.state?.policy?.agentId || user?.uid || location.state?.userId;

        if (policyId && effectiveUserId) {
            fetchPolicy();
        } else {
            console.log("Waiting for user/policyId...", { policyId, user: user?.uid });
        }
    }, [policyId, user?.uid, location.state]);

    // Sync local policy state with Redux currentPolicy for instantaneous updates after edit
    useEffect(() => {
        if (currentPolicy && currentPolicy.id === policyId) {
            setPolicy(currentPolicy);
        }
    }, [currentPolicy, policyId]);

    // Handle phone call
    const handlePhoneCall = () => {
        if (policy?.mobile) {
            window.location.href = `tel:${policy.mobile}`;
        }
    };

    // Handle edit click
    const handleEditClick = () => {
        console.log("FAB Edit Clicked, setting editDialogOpen to true");
        setEditDialogOpen(true);
    };

    // Handle edit update
    const handleEditUpdate = () => {
        // No manual refresh needed - Redux update + the synchronization useEffect handles it instantly
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    flexDirection: 'column',
                    gap: 2
                }}
            >
                <Typography>Loading commission details...</Typography>
            </Box>
        );
    }

    if (!policy) {
        console.log("📭 No policy passed in location state:", location);
        return <p>No policy data provided.</p>;
    }

    // Commission calculations
    const netPremium = parseFloat(policy.netPremium) || 0;
    const discountAmount = parseFloat(policy.discount) || 0;
    const amountReceived = parseFloat(policy.paidAmount) || 0;
    const commissionRate = policy.commissionRate || 0;
    const tdsRate = policy.tdsRate || 2;

    let baseCommission, totalCommission;

    if (policy.policyCategory === 'motor') {
        const odCommissionAmount = parseFloat(policy.odCommissionAmount) || 0;
        const tpCommissionAmount = parseFloat(policy.tpCommissionAmount) || 0;
        baseCommission = odCommissionAmount + tpCommissionAmount;
    } else {
        if (commissionRate === 0) {
            baseCommission = 0;
        } else {
            baseCommission = (netPremium * (commissionRate / 100));
        }
    }

    // Total Commission = Gross Commission * (1 - TDS%)
    // Total Commission: Use stored value if available, else calculate
    const calculatedCommission = baseCommission * (1 - (tdsRate / 100));
    totalCommission = policy.totalCommission !== undefined && policy.totalCommission !== null
        ? Math.max(0, parseFloat(policy.totalCommission))
        : Math.max(0, calculatedCommission);

    // If we have a total commission but baseCommission is 0 (e.g. from bulk upload),
    // derive baseCommission for UI consistency
    if (totalCommission > 0 && baseCommission === 0) {
        baseCommission = totalCommission / (1 - (tdsRate / 100));
    }

    return (
        <Box
            ref={contentRef}
            className="mobile-layout"
            sx={{
                width: '100vw',
                height: '100vh',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden',
                overscrollBehavior: 'none',
                pb: '0 !important'
            }}
        >

            {/* Client card */}
            <Box
                sx={{
                    width: '100%',
                    height: { xs: 70, sm: 60 },
                    px: { xs: 2, sm: 1.5 },
                    py: { xs: 1.5, sm: 1.5 },
                    backgroundColor: '#0063F7',
                    color: '#FFFFFF',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                    mx: 0,
                    borderRadius: 0,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1000
                }}
            >
                <Avatar sx={{ width: 40, height: 40, bgcolor: '#E0EBFF', color: '#0063F7', mr: { xs: 2, sm: 2 }, flexShrink: 0 }}>
                    <PersonIcon sx={{ fontSize: 24 }} />
                </Avatar>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    mr: 2
                }}>
                    <Typography sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 500,
                        fontSize: { xs: '14px', sm: '15px' },
                        lineHeight: '1.2',
                        letterSpacing: '0.01em',
                        opacity: 1,
                        textAlign: 'left',
                        mb: '2px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                    }}>{policy.name || '-'}</Typography>
                    <Typography sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 400,
                        fontSize: { xs: '12px', sm: '13px' },
                        lineHeight: '1.2',
                        letterSpacing: '0.01em',
                        color: '#FFFFFF',
                        opacity: 0.9,
                        textAlign: 'left',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                    }}>{policy.mobile || '-'}</Typography>
                </Box>

                {policy.referredBy && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        flexShrink: 0,
                        maxWidth: '35%',
                        mr: 0.5
                    }}>
                        <Typography sx={{
                            fontFamily: 'Roboto',
                            fontWeight: 400,
                            fontSize: { xs: '12px', sm: '13px' },
                            lineHeight: '1.2',
                            letterSpacing: '0.01em',
                            color: '#FFFFFF',
                            opacity: 0.9,
                            textAlign: 'right',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%'
                        }}>
                            Ref: {policy.referredBy}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Main content container */}
            <Box
                className="scroll-container"
                sx={{
                    flex: 1,
                    overflowY: 'scroll',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehavior: 'contain',
                    backgroundColor: '#f5f5f5',
                    paddingBottom: '120px', // ✅ space for FAB
                }}
            >
                <Box
                    sx={{
                        width: 'calc(100% - 32px) !important',
                        background: '#fff',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        px: 2,
                        pt: 2,
                        pb: 3, // ✅ normal padding
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        gap: 0,
                        overflow: 'visible',
                        mx: 2,
                        mt: { xs: '78px', sm: '68px' },
                        boxSizing: 'border-box',
                        maxWidth: 'calc(100vw - 32px) !important',
                        minWidth: 'auto !important',
                        flex: 'none !important'
                    }}
                >
                    {/* Details sections */}
                    <Box sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        px: 0,
                        py: 1
                    }}>
                        {/* Vehicle Details Section */}
                        {policy?.policyCategory === 'motor' && (
                            <Box sx={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1
                            }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontSize: '18px !important',
                                        fontWeight: '600 !important',
                                        color: '#1F2937',
                                        letterSpacing: '0.025em',
                                        px: 0
                                    }}
                                >
                                    Vehicle Details
                                </Typography>

                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: { xs: 2, sm: 3 },
                                    mt: 2
                                }}>
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                        mb: 2
                                    }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <DirectionsCarIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                                            <Typography
                                                sx={{
                                                    fontSize: { xs: '14px !important', sm: '16px !important' },
                                                    fontWeight: 500,
                                                    color: "#6B7280",
                                                    textTransform: 'none',
                                                    letterSpacing: '0.05em'
                                                }}
                                            >
                                                Vehicle Type
                                            </Typography>
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '18px !important', sm: '19px !important' },
                                                fontWeight: 600,
                                                color: "#111827",
                                                mb: 0.5,
                                                lineHeight: 1.3
                                            }}
                                        >
                                            {getVehicleTypeDisplayName(policy.vehicleType)}
                                        </Typography>
                                    </Box>

                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                        mb: 2
                                    }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <NumbersIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                                            <Typography
                                                sx={{
                                                    fontSize: { xs: '14px !important', sm: '16px !important' },
                                                    fontWeight: 500,
                                                    color: "#6B7280",
                                                    textTransform: 'none',
                                                    letterSpacing: '0.05em'
                                                }}
                                            >
                                                Vehicle Number
                                            </Typography>
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '18px !important', sm: '19px !important' },
                                                fontWeight: 600,
                                                color: "#111827",
                                                mb: 0.5,
                                                lineHeight: 1.3
                                            }}
                                        >
                                            {policy.vehicleNumber || '-'}
                                        </Typography>
                                    </Box>

                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                        mb: 2
                                    }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <HandymanIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                                            <Typography
                                                sx={{
                                                    fontSize: { xs: '14px !important', sm: '16px !important' },
                                                    fontWeight: 500,
                                                    color: "#6B7280",
                                                    textTransform: 'none',
                                                    letterSpacing: '0.05em'
                                                }}
                                            >
                                                Make & Model
                                            </Typography>
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '18px !important', sm: '19px !important' },
                                                fontWeight: 600,
                                                color: "#111827",
                                                mb: 0.5,
                                                lineHeight: 1.3
                                            }}
                                        >
                                            {policy.vehicleMake && policy.vehicleModel ?
                                                `${policy.vehicleMake} ${policy.vehicleModel}`.trim() :
                                                (policy.vehicleMake || policy.vehicleModel || '-')
                                            }
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Policy Details Section */}
                        <Box sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1
                        }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: '18px !important',
                                    fontWeight: '600 !important',
                                    color: '#1F2937',
                                    letterSpacing: '0.025em',
                                    px: 0
                                }}
                            >
                                Policy Details
                            </Typography>

                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: { xs: 2, sm: 3 },
                                mt: 2
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    mb: 2
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <BusinessIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '14px !important', sm: '16px !important' },
                                                fontWeight: 500,
                                                color: "#6B7280",
                                                textTransform: 'none',
                                                letterSpacing: '0.05em'
                                            }}
                                        >
                                            Company
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '18px !important', sm: '19px !important' },
                                            fontWeight: 600,
                                            color: "#111827",
                                            mb: 0.5,
                                            lineHeight: 1.3
                                        }}
                                    >
                                        {policy.policyCompany || '-'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: { xs: 2, sm: 3 },
                                mt: 1
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    mb: 2,
                                    minWidth: 0,
                                    overflow: 'hidden'
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <ListAltIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '14px !important', sm: '16px !important' },
                                                fontWeight: 500,
                                                color: "#6B7280",
                                                textTransform: 'none',
                                                letterSpacing: '0.05em'
                                            }}
                                        >
                                            Policy No
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '18px !important', sm: '19px !important' },
                                            fontWeight: 600,
                                            color: "#111827",
                                            mb: 0.5,
                                            lineHeight: 1.3,
                                            wordBreak: 'break-all',
                                            overflowWrap: 'anywhere'
                                        }}
                                    >
                                        {policy.policyNumber || '-'}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    mb: 2
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <DescriptionIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '14px !important', sm: '16px !important' },
                                                fontWeight: 500,
                                                color: "#6B7280",
                                                textTransform: 'none',
                                                letterSpacing: '0.05em'
                                            }}
                                        >
                                            Policy Type
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '18px !important', sm: '19px !important' },
                                            fontWeight: 600,
                                            color: "#111827",
                                            mb: 0.5,
                                            lineHeight: 1.3
                                        }}
                                    >
                                        {policy.policyType || '-'}
                                    </Typography>
                                </Box>

                                {policy?.policyCategory !== 'motor' && (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                        mb: 2
                                    }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <TagIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                                            <Typography
                                                sx={{
                                                    fontSize: { xs: '14px !important', sm: '16px !important' },
                                                    fontWeight: 500,
                                                    color: "#6B7280",
                                                    textTransform: 'none',
                                                    letterSpacing: '0.05em'
                                                }}
                                            >
                                                Sum Insured
                                            </Typography>
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '18px !important', sm: '19px !important' },
                                                fontWeight: 600,
                                                color: "#111827",
                                                mb: 0.5,
                                                lineHeight: 1.3
                                            }}
                                        >
                                            ₹ {policy.sumInsured || '-'}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Payment Details Section */}
                        <Box sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1
                        }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: '18px !important',
                                    fontWeight: '600 !important',
                                    color: '#1F2937',
                                    letterSpacing: '0.025em',
                                    px: 0
                                }}
                            >
                                Payment Details
                            </Typography>

                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: { xs: 2, sm: 3 },
                                mt: 2
                            }}>
                                {policy.policyCategory === 'motor' && (
                                    <>
                                        {policy.policyType !== "Liability" && (
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                                mb: 2
                                            }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <CurrencyRupeeIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                                                    <Typography
                                                        sx={{
                                                            fontSize: { xs: '14px !important', sm: '16px !important' },
                                                            fontWeight: 500,
                                                            color: "#6B7280",
                                                            textTransform: 'none',
                                                            letterSpacing: '0.05em'
                                                        }}
                                                    >
                                                        OD Premium
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: { xs: '18px !important', sm: '19px !important' },
                                                        fontWeight: 600,
                                                        color: "#111827",
                                                        mb: 0.5,
                                                        lineHeight: 1.3
                                                    }}
                                                >
                                                    ₹ {parseFloat(policy.odPremium || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Box>
                                        )}

                                        {(policy.policyType === "Package" || policy.policyType === "Liability") && (
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                                mb: 2
                                            }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <CurrencyRupeeIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                                                    <Typography
                                                        sx={{
                                                            fontSize: { xs: '14px !important', sm: '16px !important' },
                                                            fontWeight: 500,
                                                            color: "#6B7280",
                                                            textTransform: 'none',
                                                            letterSpacing: '0.05em'
                                                        }}
                                                    >
                                                        TP Premium
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: { xs: '18px !important', sm: '19px !important' },
                                                        fontWeight: 600,
                                                        color: "#111827",
                                                        mb: 0.5,
                                                        lineHeight: 1.3
                                                    }}
                                                >
                                                    ₹ {parseFloat(policy.tpPremium || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Box>
                                        )}
                                    </>
                                )}

                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    mb: 2
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <CurrencyRupeeIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '14px !important', sm: '16px !important' },
                                                fontWeight: 500,
                                                color: "#6B7280",
                                                textTransform: 'none',
                                                letterSpacing: '0.05em'
                                            }}
                                        >
                                            Net Premium
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '18px !important', sm: '19px !important' },
                                            fontWeight: 600,
                                            color: "#111827",
                                            mb: 0.5,
                                            lineHeight: 1.3
                                        }}
                                    >
                                        ₹ {parseFloat(policy.netPremium || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    mb: 2
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <CurrencyRupeeIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '14px !important', sm: '16px !important' },
                                                fontWeight: 500,
                                                color: "#6B7280",
                                                textTransform: 'none',
                                                letterSpacing: '0.05em'
                                            }}
                                        >
                                            Total Premium
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '18px !important', sm: '19px !important' },
                                            fontWeight: 600,
                                            color: "#111827",
                                            mb: 0.5,
                                            lineHeight: 1.3
                                        }}
                                    >
                                        ₹ {parseFloat(policy.premiumAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    mb: 2
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <CurrencyRupeeIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '14px !important', sm: '16px !important' },
                                                fontWeight: 500,
                                                color: "#6B7280",
                                                textTransform: 'none',
                                                letterSpacing: '0.05em'
                                            }}
                                        >
                                            Customer Discount
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '18px !important', sm: '19px !important' },
                                            fontWeight: 600,
                                            color: "#111827",
                                            mb: 0.5,
                                            lineHeight: 1.3
                                        }}
                                    >
                                        ₹ {parseFloat(policy.discount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    mb: 2
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <CurrencyRupeeIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '14px !important', sm: '16px !important' },
                                                fontWeight: 500,
                                                color: "#6B7280",
                                                textTransform: 'none',
                                                letterSpacing: '0.05em'
                                            }}
                                        >
                                            Amount Payable
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '18px !important', sm: '19px !important' },
                                            fontWeight: 600,
                                            color: "#111827",
                                            mb: 0.5,
                                            lineHeight: 1.3
                                        }}
                                    >
                                        ₹ {parseFloat(policy.amountPayable || (parseFloat(policy.premiumAmount) - (parseFloat(policy.discount) || 0))).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    mb: 2
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <SouthWestIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '14px !important', sm: '16px !important' },
                                                fontWeight: 500,
                                                color: "#6B7280",
                                                textTransform: 'none',
                                                letterSpacing: '0.05em'
                                            }}
                                        >
                                            Amount Received
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '18px !important', sm: '19px !important' },
                                            fontWeight: 600,
                                            color: "#111827",
                                            mb: 0.5,
                                            lineHeight: 1.3
                                        }}
                                    >
                                        ₹ {parseFloat(policy.paidAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    mb: 2
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <AccountBalanceWalletIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '14px !important', sm: '16px !important' },
                                                fontWeight: 500,
                                                color: "#6B7280",
                                                textTransform: 'none',
                                                letterSpacing: '0.05em'
                                            }}
                                        >
                                            Balance Amount
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '18px !important', sm: '19px !important' },
                                            fontWeight: 600,
                                            color: "#E11D48",
                                            mb: 0.5,
                                            lineHeight: 1.3
                                        }}
                                    >
                                        ₹ {Math.max((Number(policy.amountPayable || (parseFloat(policy.premiumAmount) - (parseFloat(policy.discount) || 0))) || 0) - (Number(policy.paidAmount) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    mb: 2
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <AccountBalanceWalletIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '14px !important', sm: '16px !important' },
                                                fontWeight: 500,
                                                color: "#6B7280",
                                                textTransform: 'none',
                                                letterSpacing: '0.05em'
                                            }}
                                        >
                                            Mode of Payment
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '18px !important', sm: '19px !important' },
                                            fontWeight: 600,
                                            color: "#111827",
                                            mb: 0.5,
                                            lineHeight: 1.3
                                        }}
                                    >
                                        {policy.modeOfPayment || '-'}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    mb: 2
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <ListAltIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '14px !important', sm: '16px !important' },
                                                fontWeight: 500,
                                                color: "#6B7280",
                                                textTransform: 'none',
                                                letterSpacing: '0.05em'
                                            }}
                                        >
                                            Remarks
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '16px !important', sm: '17px !important' },
                                            fontWeight: 400,
                                            color: "#111827",
                                            mb: 0.5,
                                            lineHeight: 1.3,
                                            whiteSpace: 'pre-wrap'
                                        }}
                                    >
                                        {policy.remarks || '-'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Commission Details Section */}
                        <Box sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1
                        }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: '18px !important',
                                    fontWeight: '600 !important',
                                    color: '#1F2937',
                                    letterSpacing: '0.025em',
                                    px: 0
                                }}
                            >
                                Commission Details
                            </Typography>

                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: 3,
                                mt: 2
                            }}>
                                {policy.policyCategory === 'motor' ? (
                                    <>
                                        {policy.policyType !== "Liability" && (
                                            <>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography sx={{
                                                        fontSize: { xs: '14px !important', sm: '16px !important' },
                                                        fontWeight: 500,
                                                        color: "#6B7280",
                                                        textTransform: 'none',
                                                        letterSpacing: '0.05em',
                                                        px: 0
                                                    }}>OD Rate</Typography>
                                                    <Typography sx={{
                                                        fontSize: { xs: '18px !important', sm: '19px !important' },
                                                        fontWeight: 600,
                                                        color: "#111827",
                                                        lineHeight: 1.3
                                                    }}>{policy.odCommissionRate || 0}%</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography sx={{
                                                        fontSize: { xs: '14px !important', sm: '16px !important' },
                                                        fontWeight: 500,
                                                        color: "#6B7280",
                                                        textTransform: 'none',
                                                        letterSpacing: '0.05em'
                                                    }}>OD Amount</Typography>
                                                    <Typography sx={{
                                                        fontSize: { xs: '18px !important', sm: '19px !important' },
                                                        fontWeight: 600,
                                                        color: "#111827",
                                                        lineHeight: 1.3
                                                    }}>₹ {parseFloat(policy.odCommissionAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                                </Box>
                                            </>
                                        )}
                                        {(policy.policyType === "Package" || policy.policyType === "Liability") && (
                                            <>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography sx={{
                                                        fontSize: { xs: '14px !important', sm: '16px !important' },
                                                        fontWeight: 500,
                                                        color: "#6B7280",
                                                        textTransform: 'none',
                                                        letterSpacing: '0.05em'
                                                    }}>TP Rate</Typography>
                                                    <Typography sx={{
                                                        fontSize: { xs: '18px !important', sm: '19px !important' },
                                                        fontWeight: 600,
                                                        color: "#111827",
                                                        lineHeight: 1.3
                                                    }}>{policy.tpCommissionRate || 0}%</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography sx={{
                                                        fontSize: { xs: '14px !important', sm: '16px !important' },
                                                        fontWeight: 500,
                                                        color: "#6B7280",
                                                        textTransform: 'none',
                                                        letterSpacing: '0.05em'
                                                    }}>TP Amount</Typography>
                                                    <Typography sx={{
                                                        fontSize: { xs: '18px !important', sm: '19px !important' },
                                                        fontWeight: 600,
                                                        color: "#111827",
                                                        lineHeight: 1.3
                                                    }}>₹ {parseFloat(policy.tpCommissionAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                                </Box>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Typography sx={{
                                            fontSize: { xs: '14px !important', sm: '16px !important' },
                                            fontWeight: 500,
                                            color: "#6B7280",
                                            textTransform: 'none',
                                            letterSpacing: '0.05em'
                                        }}>Rate</Typography>
                                        <Typography sx={{
                                            fontSize: { xs: '18px !important', sm: '19px !important' },
                                            fontWeight: 600,
                                            color: "#111827",
                                            lineHeight: 1.3
                                        }}>{policy.commissionRate || 0}%</Typography>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography sx={{
                                        fontSize: { xs: '14px !important', sm: '16px !important' },
                                        fontWeight: 500,
                                        color: "#6B7280",
                                        textTransform: 'none',
                                        letterSpacing: '0.05em'
                                    }}>Gross Com</Typography>
                                    <Typography sx={{
                                        fontSize: { xs: '18px !important', sm: '19px !important' },
                                        fontWeight: 600,
                                        color: "#111827",
                                        lineHeight: 1.3
                                    }}>₹ {baseCommission.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography sx={{
                                        fontSize: { xs: '14px !important', sm: '16px !important' },
                                        fontWeight: 500,
                                        color: "#6B7280",
                                        textTransform: 'none',
                                        letterSpacing: '0.05em'
                                    }}>TDS Rate</Typography>
                                    <Typography sx={{
                                        fontSize: { xs: '18px !important', sm: '19px !important' },
                                        fontWeight: 600,
                                        color: "#111827",
                                        lineHeight: 1.3
                                    }}>{tdsRate}%</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography sx={{
                                        fontSize: { xs: '14px !important', sm: '16px !important' },
                                        fontWeight: 500,
                                        color: "#6B7280",
                                        textTransform: 'none',
                                        letterSpacing: '0.05em'
                                    }}>Total Commission</Typography>
                                    <Typography sx={{
                                        fontSize: { xs: '18px !important', sm: '19px !important' },
                                        fontWeight: 600,
                                        color: totalCommission >= 0 ? '#166534' : '#E11D48',
                                        lineHeight: 1.3
                                    }}>₹ {totalCommission.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Edit Dialog */}
            <MobileCommissionEditDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                policy={policy}
                onUpdate={handleEditUpdate}
                userId={policy?.agentId || user?.uid || location.state?.userId}
            />

            {/* Edit FAB */}
            <Fab
                color="primary"
                onClick={handleEditClick}
                sx={{
                    position: 'fixed',
                    bottom: '24px !important',
                    right: 16,
                    zIndex: 1001,
                    backgroundColor: '#0063F7 !important',
                    color: '#FFFFFF',
                    boxShadow: '0 4px 12px rgba(0, 99, 247, 0.4)',
                    '&:hover': {
                        backgroundColor: '#0052CC !important',
                        boxShadow: '0 6px 16px rgba(0, 99, 247, 0.5)',
                    }
                }}
            >
                <EditIcon />
            </Fab>

        </Box >
    );
};

export default MobileCommissionDetail;
